import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    TextField,
    FormControl,
    InputLabel,
    Chip,
    CircularProgress
} from '@mui/material';
import { Download, Assessment } from '@mui/icons-material';
import { estimatesApi, Estimate } from '../services/estimatesApi';
import { projectsApi, Project } from '../services/projectsApi';
import { customersApi, Customer } from '../services/customersApi';
import { materialsApi } from '../services/materialsApi';
import { doorsApi, Door } from '../services/doorsApi';
import { windowsApi, Window as WindowModel } from '../services/windowsApi';
import GenericPage from '../../Commun/components/GenericPage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';
import './Reports.css';

type ReportType = 'sales' | 'projects' | 'materials' | 'customers' | 'production';

// Local interface fallback if not exported
interface MaterialWithUsage {
    material: any; // Type as needed
    usageCount: number;
    stockValue: number;
}

const Reports: React.FC = () => {
    const { t } = useLanguage();
    const { getParam } = useSystemParameters();
    const currencySymbol = getParam('Currency_Symbol', 'DZD');
    const [reportType, setReportType] = useState<ReportType>('sales');
    const [loading, setLoading] = useState(false);
    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [estimates, setEstimates] = useState<Estimate[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [materials, setMaterials] = useState<any[]>([]);
    const [doors, setDoors] = useState<Door[]>([]);
    const [windows, setWindows] = useState<WindowModel[]>([]);

    useEffect(() => {
        const today = new Date();
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        setDateFrom(firstDay.toISOString().split('T')[0]);
        setDateTo(today.toISOString().split('T')[0]);
        loadData();
    }, []);

    useEffect(() => {
        loadReportData();
    }, [reportType, dateFrom, dateTo]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [projectsRes, customersRes, materialsRes, doorsRes, windowsRes] = await Promise.all([
                projectsApi.getAll(),
                customersApi.getAll(),
                materialsApi.getAll(),
                doorsApi.getAll(),
                windowsApi.getAll(),
            ]);
            setProjects(projectsRes.data || []);
            setCustomers(customersRes.data || []);
            setMaterials(materialsRes.data || []);
            setDoors(doorsRes.data || []);
            setWindows(windowsRes.data || []);
        } catch (error) {
            console.error('Error loading data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadReportData = async () => {
        try {
            setLoading(true);
            if (reportType === 'sales') {
                const response = await estimatesApi.getAll();
                let filtered = response.data || [];

                if (dateFrom) {
                    filtered = filtered.filter(
                        (e: Estimate) => new Date(e.estimateDate) >= new Date(dateFrom)
                    );
                }
                if (dateTo) {
                    filtered = filtered.filter(
                        (e: Estimate) => new Date(e.estimateDate) <= new Date(dateTo + 'T23:59:59')
                    );
                }

                setEstimates(filtered);
            }
        } catch (error) {
            console.error('Error loading report data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value: number | undefined | null) => {
        const val = value || 0;
        return `${currencySymbol}${val.toFixed(2)}`;
    };

    const formatDate = (dateInput: string | Date | undefined) => {
        if (!dateInput) return 'N/A';
        const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
        return date.toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'approved': return 'success';
            case 'sent': return 'primary';
            case 'rejected': return 'error';
            default: return 'default';
        }
    };

    // Calculations
    const salesStats = {
        totalRevenue: estimates.reduce((sum, e) => sum + e.totalAmount, 0),
        totalEstimates: estimates.length,
        approved: estimates.filter((e) => e.status.toLowerCase() === 'approved').length,
        pending: estimates.filter((e) => e.status.toLowerCase() === 'sent').length,
        averageValue: estimates.length > 0 ? estimates.reduce((sum, e) => sum + e.totalAmount, 0) / estimates.length : 0,
    };

    const projectStats = {
        total: projects.filter((p) => p.isActive).length,
        completed: projects.filter((p) => !p.isActive).length,
        totalDoors: doors.length,
        totalWindows: windows.length,
    };

    const materialUsage = materials.map((material) => {
        const usedInDoors = doors.filter((d) => d.materialType === material.materialType).length;
        const usedInWindows = windows.filter((w) => w.materialType === material.materialType).length;
        return {
            material,
            usageCount: usedInDoors + usedInWindows,
            stockValue: (material.stockQuantity || 0) * (material.unitPrice || 0),
        };
    });

    const customerStats = customers.map((customer) => {
        const customerProjects = projects.filter((p) => p.customerId === customer.id);
        const customerEstimates = estimates.filter((e) => customerProjects.some((p) => p.id === e.projectId));
        return {
            customer,
            projectCount: customerProjects.length,
            estimateCount: customerEstimates.length,
            totalValue: customerEstimates.reduce((sum, e) => sum + e.totalAmount, 0),
        };
    });

    const exportToCSV = () => {
        let csvContent = '';
        let filename = '';

        const escapeCsv = (val: any) => `"${String(val || '').replace(/"/g, '""')}"`;

        switch (reportType) {
            case 'sales':
                csvContent = 'Estimate Number,Date,Project,Status,Subtotal,Tax,Total\n';
                estimates.forEach((est) => {
                    const project = projects.find((p) => p.id === est.projectId);
                    csvContent += `${escapeCsv(est.estimateNumber)},${escapeCsv(est.estimateDate)},${escapeCsv(project?.name)},${escapeCsv(est.status)},${est.subTotal},${est.taxAmount},${est.totalAmount}\n`;
                });
                filename = `sales-report-${dateFrom}-${dateTo}.csv`;
                break;
            case 'projects':
                csvContent = 'Project Name,Customer,Created Date,Doors,Windows,Status\n';
                projects.forEach((project) => {
                    const customer = customers.find((c) => c.id === project.customerId);
                    const projectDoors = doors.filter((d) => d.projectId === project.id).length;
                    const projectWindows = windows.filter((w) => w.projectId === project.id).length;
                    csvContent += `${escapeCsv(project.name)},${escapeCsv(customer?.name)},${escapeCsv(project.createdDate)},${projectDoors},${projectWindows},${project.isActive ? 'Active' : 'Completed'}\n`;
                });
                filename = 'projects-report.csv';
                break;
            case 'materials':
                csvContent = 'Material Name,Code,Type,Stock Quantity,Unit Price,Stock Value,Usage Count\n';
                materialUsage.forEach((item) => {
                    csvContent += `${escapeCsv(item.material.name)},${escapeCsv(item.material.code)},${item.material.materialType},${item.material.stockQuantity},${item.material.unitPrice},${item.stockValue},${item.usageCount}\n`;
                });
                filename = 'materials-report.csv';
                break;
            case 'customers':
                csvContent = 'Customer Name,Company,Email,Phone,Projects,Estimates,Total Value\n';
                customerStats.forEach((stat) => {
                    csvContent += `${escapeCsv(stat.customer.name)},${escapeCsv(stat.customer.companyName)},${escapeCsv(stat.customer.email)},${escapeCsv(stat.customer.phone)},${stat.projectCount},${stat.estimateCount},${stat.totalValue}\n`;
                });
                filename = 'customers-report.csv';
                break;
            case 'production':
                // Simplified production CSV
                csvContent = 'Total Items,Total Doors,Total Windows\n';
                csvContent += `${doors.length + windows.length},${doors.length},${windows.length}\n`;
                filename = 'production-report.csv';
                break;
        }

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    };

    const renderStatCard = (label: string, value: string | number, color: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' = 'primary') => (
        <Card>
            <CardContent>
                <Typography color="text.secondary" gutterBottom variant="overline">{label}</Typography>
                <Typography variant="h5" color={`${color}.main`} fontWeight="bold">{value}</Typography>
            </CardContent>
        </Card>
    );

    return (
        <GenericPage
            title={t('maker.reports.title')}
            icon={<Assessment />}
            footerEnabled={false}
            additionalActions={
                <Button variant="outlined" startIcon={<Download />} onClick={exportToCSV}>
                    {t('maker.reports.export')}
                </Button>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{t('maker.reports.type.label')}</InputLabel>
                        <Select
                            value={reportType}
                            label={t('maker.reports.type.label')}
                            onChange={(e) => setReportType(e.target.value as ReportType)}
                        >
                            <MenuItem value="sales">{t('maker.reports.type.sales')}</MenuItem>
                            <MenuItem value="projects">{t('maker.reports.type.projects')}</MenuItem>
                            <MenuItem value="materials">{t('maker.reports.type.materials')}</MenuItem>
                            <MenuItem value="customers">{t('maker.reports.type.customers')}</MenuItem>
                            <MenuItem value="production">{t('maker.reports.type.production')}</MenuItem>
                        </Select>
                    </FormControl>

                    {reportType === 'sales' && (
                        <>
                            <TextField label={t('maker.reports.date.from')} type="date" size="small" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <TextField label={t('maker.reports.date.to')} type="date" size="small" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                        </>
                    )}
                </Paper>

                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
                    ) : (
                        <>
                            {reportType === 'sales' && (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={6} md={2.4}>{renderStatCard(t('maker.reports.stat.revenue'), formatCurrency(salesStats.totalRevenue), 'success')}</Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>{renderStatCard(t('maker.reports.stat.estimates'), salesStats.totalEstimates, 'info')}</Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>{renderStatCard(t('maker.reports.stat.approved'), salesStats.approved, 'success')}</Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>{renderStatCard(t('maker.reports.stat.pending'), salesStats.pending, 'warning')}</Grid>
                                        <Grid item xs={12} sm={6} md={2.4}>{renderStatCard(t('maker.reports.stat.avg_value'), formatCurrency(salesStats.averageValue), 'primary')}</Grid>
                                    </Grid>
                                    <TableContainer component={Paper}>
                                        <Table stickyHeader>
                                            <TableHead><TableRow><TableCell>{t('maker.reports.table.estimate')}</TableCell><TableCell>{t('maker.reports.table.date')}</TableCell><TableCell>{t('maker.reports.table.project')}</TableCell><TableCell>{t('maker.reports.table.status')}</TableCell><TableCell align="right">{t('maker.reports.table.total')}</TableCell></TableRow></TableHead>
                                            <TableBody>
                                                {estimates.map(est => (
                                                    <TableRow key={est.id}>
                                                        <TableCell>{est.estimateNumber}</TableCell>
                                                        <TableCell>{formatDate(est.estimateDate)}</TableCell>
                                                        <TableCell>{projects.find(p => p.id === est.projectId)?.name || 'N/A'}</TableCell>
                                                        <TableCell><Chip label={est.status} color={getStatusColor(est.status) as any} size="small" /></TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(est.totalAmount)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {reportType === 'projects' && (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.total_projects'), projectStats.total, 'primary')}</Grid>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.completed'), projectStats.completed, 'success')}</Grid>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.total_items'), projectStats.totalDoors + projectStats.totalWindows, 'info')}</Grid>
                                    </Grid>
                                    <TableContainer component={Paper}>
                                        <Table stickyHeader>
                                            <TableHead><TableRow><TableCell>{t('maker.reports.table.project')}</TableCell><TableCell>{t('maker.reports.table.customer')}</TableCell><TableCell>{t('maker.reports.table.created')}</TableCell><TableCell align="center">{t('maker.reports.table.items')}</TableCell><TableCell>{t('maker.reports.table.status')}</TableCell></TableRow></TableHead>
                                            <TableBody>
                                                {projects.map(prj => {
                                                    const dCount = doors.filter(d => d.projectId === prj.id).length;
                                                    const wCount = windows.filter(w => w.projectId === prj.id).length;
                                                    return (
                                                        <TableRow key={prj.id}>
                                                            <TableCell>{prj.name}</TableCell>
                                                            <TableCell>{customers.find(c => c.id === prj.customerId)?.name || 'N/A'}</TableCell>
                                                            <TableCell>{formatDate(prj.createdDate)}</TableCell>
                                                            <TableCell align="center">{dCount} {t('maker.reports.stat.doors')} / {wCount} {t('maker.reports.stat.windows')}</TableCell>
                                                            <TableCell><Chip label={prj.isActive ? t('maker.projects.filter.active') : t('maker.reports.stat.completed')} color={prj.isActive ? 'success' : 'default'} size="small" /></TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {reportType === 'materials' && (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.total_materials'), materials.length, 'primary')}</Grid>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.stock_value'), formatCurrency(materialUsage.reduce((s, i) => s + i.stockValue, 0)), 'warning')}</Grid>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.active'), materials.filter(m => m.isActive).length, 'success')}</Grid>
                                    </Grid>
                                    <TableContainer component={Paper}>
                                        <Table stickyHeader>
                                            <TableHead><TableRow><TableCell>{t('maker.reports.table.name')}</TableCell><TableCell>{t('maker.reports.table.code')}</TableCell><TableCell>{t('maker.reports.table.stock')}</TableCell><TableCell align="right">{t('maker.reports.table.value')}</TableCell><TableCell align="center">{t('maker.reports.table.usage')}</TableCell></TableRow></TableHead>
                                            <TableBody>
                                                {materialUsage.map(item => (
                                                    <TableRow key={item.material.id}>
                                                        <TableCell>{item.material.name}</TableCell>
                                                        <TableCell>{item.material.code || '-'}</TableCell>
                                                        <TableCell>{item.material.stockQuantity}</TableCell>
                                                        <TableCell align="right">{formatCurrency(item.stockValue)}</TableCell>
                                                        <TableCell align="center">{item.usageCount}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {reportType === 'customers' && (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={6} md={3}>{renderStatCard(t('maker.reports.stat.total_customers'), customerStats.length, 'primary')}</Grid>
                                        <Grid item xs={12} sm={6} md={3}>{renderStatCard(t('maker.reports.stat.project_value'), formatCurrency(customerStats.reduce((s, c) => s + c.totalValue, 0)), 'money' as any)}</Grid>
                                    </Grid>
                                    <TableContainer component={Paper}>
                                        <Table stickyHeader>
                                            <TableHead><TableRow><TableCell>{t('maker.reports.table.name')}</TableCell><TableCell>{t('maker.reports.table.company')}</TableCell><TableCell>{t('maker.reports.table.estimates')}</TableCell><TableCell align="right">{t('maker.reports.table.value')}</TableCell></TableRow></TableHead>
                                            <TableBody>
                                                {customerStats.map(stat => (
                                                    <TableRow key={stat.customer.id}>
                                                        <TableCell>{stat.customer.name}</TableCell>
                                                        <TableCell>{stat.customer.companyName || '-'}</TableCell>
                                                        <TableCell>{stat.estimateCount}</TableCell>
                                                        <TableCell align="right">{formatCurrency(stat.totalValue)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            )}

                            {reportType === 'production' && (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.total_items'), doors.length + windows.length, 'primary')}</Grid>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.doors'), doors.length, 'info')}</Grid>
                                        <Grid item xs={12} sm={4}>{renderStatCard(t('maker.reports.stat.windows'), windows.length, 'info')}</Grid>
                                    </Grid>
                                    <Paper sx={{ p: 2 }}>
                                        <Typography variant="h6" gutterBottom>{t('maker.reports.dist.material')}</Typography>
                                        {/* Simple text representation for now, could catch charts later */}
                                        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                            {['Aluminum', 'PVC', 'Wood', 'Steel'].map((type, idx) => {
                                                const typeId = idx + 1;
                                                const count = doors.filter(d => d.materialType === typeId).length + windows.filter(w => w.materialType === typeId).length;
                                                const total = doors.length + windows.length;
                                                const pct = total > 0 ? (count / total * 100).toFixed(1) : 0;
                                                return (
                                                    <Box key={type} sx={{ minWidth: 100 }}>
                                                        <Typography variant="body2" color="text.secondary">{type}</Typography>
                                                        <Typography variant="h4">{count}</Typography>
                                                        <Typography variant="caption">{pct}%</Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Paper>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </GenericPage>
    );
};

export default Reports;
